/**
 * Country registry for the E-Signature Legality Guide.
 *
 * ⚠️ GENERATED for the family-only rows, hand-maintained for the rest. The
 * generator lives in the session scratchpad and is disposable; THIS FILE is the
 * artifact. Edit it directly when upgrading a row's depth.
 *
 * ── How a row earns its place ──
 * Every row carries `sourceTable`, and that field is the entire integrity
 * model. A `family-only` row asserts ONE thing: that the country appears in a
 * named published table. It does not name a statute, characterise the law, or
 * imply anything about tiers — so it cannot be wrong in a way a reader could
 * rely on.
 *
 * Upgrading a row to `row` (statute named) or `full` (own page) requires
 * opening the statute and confirming the URL resolves. That is a Tier B/A
 * upgrade and is done by hand, never inferred.
 *
 * ⚠️ Family placement follows PUBLISHED-TABLE membership, not general
 * knowledge. Singapore sits in `uncitral-model` because UNCITRAL's table says
 * so — even though its Electronic Transactions Act is Commonwealth-shaped —
 * because we have not yet opened that Act. Moving it to `commonwealth-eta` is
 * part of the upgrade, not a free reclassification. Every placement here is
 * therefore true, if sometimes less specific than it could be.
 *
 * ── Reconciliation against UNCITRAL's "88 States" (checked 2026-08-10) ──
 *      79  uncitral-model rows here
 *    +  4  MLEC States that are EU members, so placed in the eidas family
 *           (France, Ireland, Malta, Slovenia)
 *    +  5  MLEC States held as Tier A seeds with a verified statute
 *           (Australia, Canada, India, United Kingdom, United States)
 *    = 88  ✅ exact
 *
 * ⚠️ FOUR States appear in that table as PARENT rows above their
 * sub-jurisdictions — Australia, Canada, the United Kingdom and the United
 * States. A naive scrape returns their sub-units and drops the parent, which
 * undercounts by up to four and is easy to mistake for missing data. All four
 * are Tier A seeds here.
 *
 * ⚠️ Sub-national entries in UNCITRAL's table are excluded: US states,
 * Canadian provinces, Australian territories, UK crown dependencies and
 * overseas territories, the Abu Dhabi Global Market, and the Hong Kong and
 * Macao SARs. UNCITRAL counts 88 States and 171 JURISDICTIONS; conflating the
 * two is how the "180+ countries" claim was born. This registry counts
 * countries.
 *
 * ⚠️ The European Union is not a row. eIDAS is modelled as a family; an "EU"
 * entry would double-count all 27 member states in the coverage figure.
 */
import type { LegalityCountry } from "./types";

export const LEGALITY_COUNTRIES: LegalityCountry[] = [
  /* ── Tier A: full pages, verified statutes ── */
  {
    iso2: "US",
    name: "United States",
    familyId: "united-states",
    depth: "full",
    sourceTable: "national-statute",
    statuteName: "ESIGN Act, Pub. L. 106-229 (2000), with state UETA",
    statuteUrl:
      "https://www.govinfo.gov/content/pkg/PLAW-106publ229/html/PLAW-106publ229.htm",
    variantSlug: "esign-act-compliance",
    reviewed: "2026-08-10",
  },
  {
    iso2: "GB",
    name: "United Kingdom",
    familyId: "commonwealth-eta",
    depth: "full",
    sourceTable: "national-statute",
    statuteName: "Electronic Communications Act 2000",
    statuteUrl: "https://www.legislation.gov.uk/ukpga/2000/7/contents",
    variantSlug: "electronic-signature-uk-legal",
    reviewed: "2026-08-10",
  },
  {
    iso2: "AU",
    name: "Australia",
    familyId: "commonwealth-eta",
    depth: "full",
    sourceTable: "national-statute",
    statuteName: "Electronic Transactions Act 1999 (Cth)",
    statuteUrl: "https://www.legislation.gov.au/C2004A00553/latest/text",
    variantSlug: "electronic-signature-australia-legal",
    reviewed: "2026-08-10",
  },
  {
    iso2: "CA",
    name: "Canada",
    familyId: "uncitral-model",
    depth: "full",
    sourceTable: "national-statute",
    statuteName: "PIPEDA Part 2, with provincial enactments of the UECA",
    statuteUrl: "https://laws-lois.justice.gc.ca/eng/acts/P-8.6/index.html",
    variantSlug: "electronic-signature-canada-legal",
    reviewed: "2026-08-10",
  },
  {
    iso2: "IN",
    name: "India",
    familyId: "licensed-provider",
    depth: "full",
    sourceTable: "national-statute",
    statuteName: "Information Technology Act, 2000 (amended 2008)",
    statuteUrl: "https://www.indiacode.nic.in/handle/123456789/1999",
    variantSlug: "electronic-signature-india-it-act",
    localCaTierReserved: true,
    reviewed: "2026-08-10",
  },
  {
    iso2: "JP",
    name: "Japan",
    familyId: "civil-law-tiered",
    depth: "full",
    sourceTable: "national-statute",
    statuteName:
      "Act on Electronic Signatures and Certification Business (Act No. 102 of 2000)",
    // Official e-Gov text (Japanese). The standard English translation host
    // refused automated retrieval on 2026-08-10, so the authoritative source
    // is cited rather than an unverified mirror.
    statuteUrl: "https://elaws.e-gov.go.jp/document?lawid=412AC0000000102",
    variantSlug: "electronic-signature-japan-legal",
    reviewed: "2026-08-10",
  },
  {
    iso2: "BR",
    name: "Brazil",
    familyId: "civil-law-tiered",
    depth: "full",
    sourceTable: "national-statute",
    statuteName: "Lei 14.063/2020, with MP 2.200-2/2001 (ICP-Brasil)",
    statuteUrl:
      "https://www.planalto.gov.br/ccivil_03/_ato2019-2022/2020/lei/l14063.htm",
    variantSlug: "electronic-signature-brazil-legal",
    reviewed: "2026-08-10",
  },

  /* ── eIDAS: EU + EEA membership. The Regulation applies directly,
     so membership of the union IS the source. ── */
  {
    iso2: "AT",
    name: "Austria",
    familyId: "eidas",
    depth: "family-only",
    sourceTable: "eu-eea",
    reviewed: "2026-08-10",
  },
  {
    iso2: "BE",
    name: "Belgium",
    familyId: "eidas",
    depth: "family-only",
    sourceTable: "eu-eea",
    reviewed: "2026-08-10",
  },
  {
    iso2: "BG",
    name: "Bulgaria",
    familyId: "eidas",
    depth: "family-only",
    sourceTable: "eu-eea",
    reviewed: "2026-08-10",
  },
  {
    iso2: "HR",
    name: "Croatia",
    familyId: "eidas",
    depth: "family-only",
    sourceTable: "eu-eea",
    reviewed: "2026-08-10",
  },
  {
    iso2: "CY",
    name: "Cyprus",
    familyId: "eidas",
    depth: "family-only",
    sourceTable: "eu-eea",
    reviewed: "2026-08-10",
  },
  {
    iso2: "CZ",
    name: "Czechia",
    familyId: "eidas",
    depth: "family-only",
    sourceTable: "eu-eea",
    reviewed: "2026-08-10",
  },
  {
    iso2: "DK",
    name: "Denmark",
    familyId: "eidas",
    depth: "family-only",
    sourceTable: "eu-eea",
    reviewed: "2026-08-10",
  },
  {
    iso2: "EE",
    name: "Estonia",
    familyId: "eidas",
    depth: "family-only",
    sourceTable: "eu-eea",
    reviewed: "2026-08-10",
  },
  {
    iso2: "FI",
    name: "Finland",
    familyId: "eidas",
    depth: "family-only",
    sourceTable: "eu-eea",
    reviewed: "2026-08-10",
  },
  {
    iso2: "FR",
    name: "France",
    familyId: "eidas",
    depth: "family-only",
    sourceTable: "eu-eea",
    reviewed: "2026-08-10",
  },
  {
    iso2: "DE",
    name: "Germany",
    familyId: "eidas",
    depth: "family-only",
    sourceTable: "eu-eea",
    reviewed: "2026-08-10",
  },
  {
    iso2: "GR",
    name: "Greece",
    familyId: "eidas",
    depth: "family-only",
    sourceTable: "eu-eea",
    reviewed: "2026-08-10",
  },
  {
    iso2: "HU",
    name: "Hungary",
    familyId: "eidas",
    depth: "family-only",
    sourceTable: "eu-eea",
    reviewed: "2026-08-10",
  },
  {
    iso2: "IE",
    name: "Ireland",
    familyId: "eidas",
    depth: "family-only",
    sourceTable: "eu-eea",
    reviewed: "2026-08-10",
  },
  {
    iso2: "IT",
    name: "Italy",
    familyId: "eidas",
    depth: "family-only",
    sourceTable: "eu-eea",
    reviewed: "2026-08-10",
  },
  {
    iso2: "LV",
    name: "Latvia",
    familyId: "eidas",
    depth: "family-only",
    sourceTable: "eu-eea",
    reviewed: "2026-08-10",
  },
  {
    iso2: "LT",
    name: "Lithuania",
    familyId: "eidas",
    depth: "family-only",
    sourceTable: "eu-eea",
    reviewed: "2026-08-10",
  },
  {
    iso2: "LU",
    name: "Luxembourg",
    familyId: "eidas",
    depth: "family-only",
    sourceTable: "eu-eea",
    reviewed: "2026-08-10",
  },
  {
    iso2: "MT",
    name: "Malta",
    familyId: "eidas",
    depth: "family-only",
    sourceTable: "eu-eea",
    reviewed: "2026-08-10",
  },
  {
    iso2: "NL",
    name: "Netherlands",
    familyId: "eidas",
    depth: "family-only",
    sourceTable: "eu-eea",
    reviewed: "2026-08-10",
  },
  {
    iso2: "PL",
    name: "Poland",
    familyId: "eidas",
    depth: "full",
    // Placement is still EU membership — eIDAS applies directly. The statute
    // named here is the national companion act, not the source of placement.
    sourceTable: "eu-eea",
    statuteName:
      "Act of 5 September 2016 on Trust Services and Electronic Identification (Dz.U. 2016 poz. 1579)",
    // ⚠️ Resolves, and the identifier encodes the Dziennik Ustaw reference, but
    // ISAP sits behind bot protection and returns an empty body to automation,
    // so its contents were not machine-verified. A browser reaches it normally.
    statuteUrl: "https://isap.sejm.gov.pl/isap.nsf/DocDetails.xsp?id=WDU20160001579",
    variantSlug: "electronic-signature-poland-legal",
    reviewed: "2026-08-11",
  },
  {
    iso2: "PT",
    name: "Portugal",
    familyId: "eidas",
    depth: "family-only",
    sourceTable: "eu-eea",
    reviewed: "2026-08-10",
  },
  {
    iso2: "RO",
    name: "Romania",
    familyId: "eidas",
    depth: "family-only",
    sourceTable: "eu-eea",
    reviewed: "2026-08-10",
  },
  {
    iso2: "SK",
    name: "Slovakia",
    familyId: "eidas",
    depth: "family-only",
    sourceTable: "eu-eea",
    reviewed: "2026-08-10",
  },
  {
    iso2: "SI",
    name: "Slovenia",
    familyId: "eidas",
    depth: "family-only",
    sourceTable: "eu-eea",
    reviewed: "2026-08-10",
  },
  {
    iso2: "ES",
    name: "Spain",
    familyId: "eidas",
    depth: "family-only",
    sourceTable: "eu-eea",
    reviewed: "2026-08-10",
  },
  {
    iso2: "SE",
    name: "Sweden",
    familyId: "eidas",
    depth: "family-only",
    sourceTable: "eu-eea",
    reviewed: "2026-08-10",
  },
  {
    iso2: "IS",
    name: "Iceland",
    familyId: "eidas",
    depth: "family-only",
    sourceTable: "eu-eea",
    reviewed: "2026-08-10",
  },
  {
    iso2: "LI",
    name: "Liechtenstein",
    familyId: "eidas",
    depth: "family-only",
    sourceTable: "eu-eea",
    reviewed: "2026-08-10",
  },
  {
    iso2: "NO",
    name: "Norway",
    familyId: "eidas",
    depth: "family-only",
    sourceTable: "eu-eea",
    reviewed: "2026-08-10",
  },

  /* ── UNCITRAL MLEC (1996) status table, sovereign States. ── */
  {
    iso2: "AF",
    name: "Afghanistan",
    familyId: "uncitral-model",
    depth: "family-only",
    sourceTable: "uncitral-mlec",
    reviewed: "2026-08-10",
  },
  {
    iso2: "AG",
    name: "Antigua and Barbuda",
    familyId: "uncitral-model",
    depth: "family-only",
    sourceTable: "uncitral-mlec",
    reviewed: "2026-08-10",
  },
  {
    iso2: "BS",
    name: "Bahamas",
    familyId: "uncitral-model",
    depth: "family-only",
    sourceTable: "uncitral-mlec",
    reviewed: "2026-08-10",
  },
  {
    iso2: "BH",
    name: "Bahrain",
    familyId: "uncitral-model",
    depth: "family-only",
    sourceTable: "uncitral-mlec",
    reviewed: "2026-08-10",
  },
  {
    iso2: "BD",
    name: "Bangladesh",
    familyId: "uncitral-model",
    depth: "family-only",
    sourceTable: "uncitral-mlec",
    reviewed: "2026-08-10",
  },
  {
    iso2: "BB",
    name: "Barbados",
    familyId: "uncitral-model",
    depth: "family-only",
    sourceTable: "uncitral-mlec",
    reviewed: "2026-08-10",
  },
  {
    iso2: "BZ",
    name: "Belize",
    familyId: "uncitral-model",
    depth: "family-only",
    sourceTable: "uncitral-mlec",
    reviewed: "2026-08-10",
  },
  {
    iso2: "BT",
    name: "Bhutan",
    familyId: "uncitral-model",
    depth: "family-only",
    sourceTable: "uncitral-mlec",
    reviewed: "2026-08-10",
  },
  {
    iso2: "BW",
    name: "Botswana",
    familyId: "uncitral-model",
    depth: "family-only",
    sourceTable: "uncitral-mlec",
    reviewed: "2026-08-10",
  },
  {
    iso2: "BN",
    name: "Brunei Darussalam",
    familyId: "uncitral-model",
    depth: "family-only",
    sourceTable: "uncitral-mlec",
    reviewed: "2026-08-10",
  },
  {
    iso2: "CV",
    name: "Cabo Verde",
    familyId: "uncitral-model",
    depth: "family-only",
    sourceTable: "uncitral-mlec",
    reviewed: "2026-08-10",
  },
  {
    iso2: "KH",
    name: "Cambodia",
    familyId: "uncitral-model",
    depth: "family-only",
    sourceTable: "uncitral-mlec",
    reviewed: "2026-08-10",
  },
  {
    iso2: "CN",
    name: "China",
    familyId: "uncitral-model",
    depth: "family-only",
    sourceTable: "uncitral-mlec",
    reviewed: "2026-08-10",
  },
  {
    iso2: "CO",
    name: "Colombia",
    familyId: "uncitral-model",
    depth: "family-only",
    sourceTable: "uncitral-mlec",
    reviewed: "2026-08-10",
  },
  {
    iso2: "DM",
    name: "Dominica",
    familyId: "uncitral-model",
    depth: "family-only",
    sourceTable: "uncitral-mlec",
    reviewed: "2026-08-10",
  },
  {
    iso2: "DO",
    name: "Dominican Republic",
    familyId: "uncitral-model",
    depth: "family-only",
    sourceTable: "uncitral-mlec",
    reviewed: "2026-08-10",
  },
  {
    iso2: "EC",
    name: "Ecuador",
    familyId: "uncitral-model",
    depth: "family-only",
    sourceTable: "uncitral-mlec",
    reviewed: "2026-08-10",
  },
  {
    iso2: "SV",
    name: "El Salvador",
    familyId: "uncitral-model",
    depth: "family-only",
    sourceTable: "uncitral-mlec",
    reviewed: "2026-08-10",
  },
  {
    iso2: "SZ",
    name: "Eswatini",
    familyId: "uncitral-model",
    depth: "family-only",
    sourceTable: "uncitral-mlec",
    reviewed: "2026-08-10",
  },
  {
    iso2: "ET",
    name: "Ethiopia",
    familyId: "uncitral-model",
    depth: "family-only",
    sourceTable: "uncitral-mlec",
    reviewed: "2026-08-10",
  },
  {
    iso2: "FJ",
    name: "Fiji",
    familyId: "uncitral-model",
    depth: "family-only",
    sourceTable: "uncitral-mlec",
    reviewed: "2026-08-10",
  },
  {
    iso2: "GM",
    name: "Gambia",
    familyId: "uncitral-model",
    depth: "family-only",
    sourceTable: "uncitral-mlec",
    reviewed: "2026-08-10",
  },
  {
    iso2: "GH",
    name: "Ghana",
    familyId: "uncitral-model",
    depth: "family-only",
    sourceTable: "uncitral-mlec",
    reviewed: "2026-08-10",
  },
  {
    iso2: "GD",
    name: "Grenada",
    familyId: "uncitral-model",
    depth: "family-only",
    sourceTable: "uncitral-mlec",
    reviewed: "2026-08-10",
  },
  {
    iso2: "GT",
    name: "Guatemala",
    familyId: "uncitral-model",
    depth: "full",
    sourceTable: "national-statute",
    statuteName: "Decreto 47-2008, Ley para el Reconocimiento de las Comunicaciones y Firmas Electrónicas",
    // RIPD legislative entry, confirmed 2026-08-11 to be this decree and to
    // link the full text. congreso.gob.gt and rpsc.gob.gt WAF-block automated
    // access, and the widely-cited RPSC PDF returns 404.
    statuteUrl: "https://www.redipd.org/en/legislation/decreto-47-2008",
    variantSlug: "electronic-signature-guatemala-legal",
    localCaTierReserved: true,
    reviewed: "2026-08-11",
  },
  {
    iso2: "GY",
    name: "Guyana",
    familyId: "uncitral-model",
    depth: "family-only",
    sourceTable: "uncitral-mlec",
    reviewed: "2026-08-10",
  },
  {
    iso2: "HT",
    name: "Haiti",
    familyId: "uncitral-model",
    depth: "family-only",
    sourceTable: "uncitral-mlec",
    reviewed: "2026-08-10",
  },
  {
    iso2: "HN",
    name: "Honduras",
    familyId: "uncitral-model",
    depth: "family-only",
    sourceTable: "uncitral-mlec",
    reviewed: "2026-08-10",
  },
  {
    iso2: "IR",
    name: "Iran (Islamic Republic of)",
    familyId: "uncitral-model",
    depth: "family-only",
    sourceTable: "uncitral-mlec",
    reviewed: "2026-08-10",
  },
  {
    iso2: "JM",
    name: "Jamaica",
    familyId: "uncitral-model",
    depth: "family-only",
    sourceTable: "uncitral-mlec",
    reviewed: "2026-08-10",
  },
  {
    iso2: "JO",
    name: "Jordan",
    familyId: "uncitral-model",
    depth: "family-only",
    sourceTable: "uncitral-mlec",
    reviewed: "2026-08-10",
  },
  {
    iso2: "KI",
    name: "Kiribati",
    familyId: "uncitral-model",
    depth: "family-only",
    sourceTable: "uncitral-mlec",
    reviewed: "2026-08-10",
  },
  {
    iso2: "KW",
    name: "Kuwait",
    familyId: "uncitral-model",
    depth: "family-only",
    sourceTable: "uncitral-mlec",
    reviewed: "2026-08-10",
  },
  {
    iso2: "LA",
    name: "Lao People's Democratic Republic",
    familyId: "uncitral-model",
    depth: "family-only",
    sourceTable: "uncitral-mlec",
    reviewed: "2026-08-10",
  },
  {
    iso2: "LR",
    name: "Liberia",
    familyId: "uncitral-model",
    depth: "family-only",
    sourceTable: "uncitral-mlec",
    reviewed: "2026-08-10",
  },
  {
    iso2: "LY",
    name: "Libya",
    familyId: "uncitral-model",
    depth: "family-only",
    sourceTable: "uncitral-mlec",
    reviewed: "2026-08-10",
  },
  {
    iso2: "MG",
    name: "Madagascar",
    familyId: "uncitral-model",
    depth: "family-only",
    sourceTable: "uncitral-mlec",
    reviewed: "2026-08-10",
  },
  {
    iso2: "MW",
    name: "Malawi",
    familyId: "uncitral-model",
    depth: "family-only",
    sourceTable: "uncitral-mlec",
    reviewed: "2026-08-10",
  },
  {
    iso2: "MY",
    name: "Malaysia",
    familyId: "uncitral-model",
    depth: "family-only",
    sourceTable: "uncitral-mlec",
    reviewed: "2026-08-10",
  },
  {
    iso2: "MV",
    name: "Maldives",
    familyId: "uncitral-model",
    depth: "family-only",
    sourceTable: "uncitral-mlec",
    reviewed: "2026-08-10",
  },
  {
    iso2: "MH",
    name: "Marshall Islands",
    familyId: "uncitral-model",
    depth: "family-only",
    sourceTable: "uncitral-mlec",
    reviewed: "2026-08-10",
  },
  {
    iso2: "MU",
    name: "Mauritius",
    familyId: "uncitral-model",
    depth: "family-only",
    sourceTable: "uncitral-mlec",
    reviewed: "2026-08-10",
  },
  {
    iso2: "MX",
    name: "Mexico",
    familyId: "uncitral-model",
    depth: "full",
    sourceTable: "national-statute",
    statuteName: "Código de Comercio, Título Segundo (arts. 89-114)",
    // WIPO Lex 18090 — Commercial Code consolidated in the DOF on 28 March
    // 2018. Confirmed on 2026-08-11 to contain the electronic commerce title.
    statuteUrl: "https://www.wipo.int/wipolex/en/legislation/details/18090",
    variantSlug: "electronic-signature-mexico-legal",
    reviewed: "2026-08-11",
  },
  {
    iso2: "MZ",
    name: "Mozambique",
    familyId: "uncitral-model",
    depth: "family-only",
    sourceTable: "uncitral-mlec",
    reviewed: "2026-08-10",
  },
  {
    iso2: "NA",
    name: "Namibia",
    familyId: "uncitral-model",
    depth: "family-only",
    sourceTable: "uncitral-mlec",
    reviewed: "2026-08-10",
  },
  {
    iso2: "NZ",
    name: "New Zealand",
    familyId: "uncitral-model",
    depth: "family-only",
    sourceTable: "uncitral-mlec",
    reviewed: "2026-08-10",
  },
  {
    iso2: "OM",
    name: "Oman",
    familyId: "uncitral-model",
    depth: "family-only",
    sourceTable: "uncitral-mlec",
    reviewed: "2026-08-10",
  },
  {
    iso2: "PK",
    name: "Pakistan",
    familyId: "uncitral-model",
    depth: "family-only",
    sourceTable: "uncitral-mlec",
    reviewed: "2026-08-10",
  },
  {
    iso2: "PA",
    name: "Panama",
    familyId: "uncitral-model",
    depth: "family-only",
    sourceTable: "uncitral-mlec",
    reviewed: "2026-08-10",
  },
  {
    iso2: "PG",
    name: "Papua New Guinea",
    familyId: "uncitral-model",
    depth: "family-only",
    sourceTable: "uncitral-mlec",
    reviewed: "2026-08-10",
  },
  {
    iso2: "PY",
    name: "Paraguay",
    familyId: "uncitral-model",
    depth: "family-only",
    sourceTable: "uncitral-mlec",
    reviewed: "2026-08-10",
  },
  {
    iso2: "PH",
    name: "Philippines",
    familyId: "uncitral-model",
    depth: "family-only",
    sourceTable: "uncitral-mlec",
    reviewed: "2026-08-10",
  },
  {
    iso2: "QA",
    name: "Qatar",
    familyId: "uncitral-model",
    depth: "family-only",
    sourceTable: "uncitral-mlec",
    reviewed: "2026-08-10",
  },
  {
    iso2: "KR",
    name: "Republic of Korea",
    familyId: "uncitral-model",
    depth: "family-only",
    sourceTable: "uncitral-mlec",
    reviewed: "2026-08-10",
  },
  {
    iso2: "RW",
    name: "Rwanda",
    familyId: "uncitral-model",
    depth: "family-only",
    sourceTable: "uncitral-mlec",
    reviewed: "2026-08-10",
  },
  {
    iso2: "KN",
    name: "Saint Kitts and Nevis",
    familyId: "uncitral-model",
    depth: "family-only",
    sourceTable: "uncitral-mlec",
    reviewed: "2026-08-10",
  },
  {
    iso2: "LC",
    name: "Saint Lucia",
    familyId: "uncitral-model",
    depth: "family-only",
    sourceTable: "uncitral-mlec",
    reviewed: "2026-08-10",
  },
  {
    iso2: "VC",
    name: "Saint Vincent and the Grenadines",
    familyId: "uncitral-model",
    depth: "family-only",
    sourceTable: "uncitral-mlec",
    reviewed: "2026-08-10",
  },
  {
    iso2: "WS",
    name: "Samoa",
    familyId: "uncitral-model",
    depth: "family-only",
    sourceTable: "uncitral-mlec",
    reviewed: "2026-08-10",
  },
  {
    iso2: "SM",
    name: "San Marino",
    familyId: "uncitral-model",
    depth: "family-only",
    sourceTable: "uncitral-mlec",
    reviewed: "2026-08-10",
  },
  {
    iso2: "SA",
    name: "Saudi Arabia",
    familyId: "uncitral-model",
    depth: "family-only",
    sourceTable: "uncitral-mlec",
    reviewed: "2026-08-10",
  },
  {
    iso2: "SC",
    name: "Seychelles",
    familyId: "uncitral-model",
    depth: "family-only",
    sourceTable: "uncitral-mlec",
    reviewed: "2026-08-10",
  },
  {
    iso2: "SL",
    name: "Sierra Leone",
    familyId: "uncitral-model",
    depth: "family-only",
    sourceTable: "uncitral-mlec",
    reviewed: "2026-08-10",
  },
  {
    iso2: "SG",
    name: "Singapore",
    familyId: "uncitral-model",
    depth: "family-only",
    sourceTable: "uncitral-mlec",
    reviewed: "2026-08-10",
  },
  {
    iso2: "ZA",
    name: "South Africa",
    familyId: "uncitral-model",
    depth: "family-only",
    sourceTable: "uncitral-mlec",
    reviewed: "2026-08-10",
  },
  {
    iso2: "LK",
    name: "Sri Lanka",
    familyId: "uncitral-model",
    depth: "family-only",
    sourceTable: "uncitral-mlec",
    reviewed: "2026-08-10",
  },
  {
    iso2: "SR",
    name: "Suriname",
    familyId: "uncitral-model",
    depth: "family-only",
    sourceTable: "uncitral-mlec",
    reviewed: "2026-08-10",
  },
  {
    iso2: "SY",
    name: "Syrian Arab Republic",
    familyId: "uncitral-model",
    depth: "family-only",
    sourceTable: "uncitral-mlec",
    reviewed: "2026-08-10",
  },
  {
    iso2: "TH",
    name: "Thailand",
    familyId: "uncitral-model",
    depth: "family-only",
    sourceTable: "uncitral-mlec",
    reviewed: "2026-08-10",
  },
  {
    iso2: "TL",
    name: "Timor-Leste",
    familyId: "uncitral-model",
    depth: "family-only",
    sourceTable: "uncitral-mlec",
    reviewed: "2026-08-10",
  },
  {
    iso2: "TG",
    name: "Togo",
    familyId: "uncitral-model",
    depth: "family-only",
    sourceTable: "uncitral-mlec",
    reviewed: "2026-08-10",
  },
  {
    iso2: "TT",
    name: "Trinidad and Tobago",
    familyId: "uncitral-model",
    depth: "family-only",
    sourceTable: "uncitral-mlec",
    reviewed: "2026-08-10",
  },
  {
    iso2: "UG",
    name: "Uganda",
    familyId: "uncitral-model",
    depth: "family-only",
    sourceTable: "uncitral-mlec",
    reviewed: "2026-08-10",
  },
  {
    iso2: "AE",
    name: "United Arab Emirates",
    familyId: "uncitral-model",
    depth: "family-only",
    sourceTable: "uncitral-mlec",
    reviewed: "2026-08-10",
  },
  {
    iso2: "TZ",
    name: "United Republic of Tanzania",
    familyId: "uncitral-model",
    depth: "family-only",
    sourceTable: "uncitral-mlec",
    reviewed: "2026-08-10",
  },
  {
    iso2: "VU",
    name: "Vanuatu",
    familyId: "uncitral-model",
    depth: "family-only",
    sourceTable: "uncitral-mlec",
    reviewed: "2026-08-10",
  },
  {
    iso2: "VE",
    name: "Venezuela (Bolivarian Republic of)",
    familyId: "uncitral-model",
    depth: "family-only",
    sourceTable: "uncitral-mlec",
    reviewed: "2026-08-10",
  },
  {
    iso2: "VN",
    name: "Viet Nam",
    familyId: "uncitral-model",
    depth: "family-only",
    sourceTable: "uncitral-mlec",
    reviewed: "2026-08-10",
  },
  {
    iso2: "ZM",
    name: "Zambia",
    familyId: "uncitral-model",
    depth: "family-only",
    sourceTable: "uncitral-mlec",
    reviewed: "2026-08-10",
  },
];
